/**
 * GA4 Analytics Service for sight3-client (X.Machines).
 *
 * Wraps gtag.js to:
 *  - Set user-scoped custom dimensions (customer_code, user_email, user_role)
 *  - Auto-emit page_view events with product_name = 'x-machines'
 *  - Provide a clean API for feature interaction tracking
 *  - Filter data to only include real users in production.
 *
 * Initialization:
 *   Call ga4Analytics.initialize({ ... }) once after authentication succeeds.
 *
 * Page Tracking:
 *   Use the `usePageTracking()` hook in your router (see hooks/usePageTracking.ts).
 *
 * Prerequisites:
 *   gtag.js must be loaded in public/index.html:
 *     <script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
 *     <script>
 *       window.dataLayer = window.dataLayer || [];
 *       function gtag(){dataLayer.push(arguments);}
 *       gtag('js', new Date());
 *       gtag('config', 'G-XXXXXXXXXX', { send_page_view: false });
 *     </script>
 */

import { type Role } from 'components/Permission/permission.types';
import { appConfiguration } from 'utils/config/app.config';
import { isExternalRole } from 'utils/validators/role.validation';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
    dataLayer: Array<Record<string, unknown>>;
  }
}

interface GA4UserContext {
  userId: string;
  userEmail: string;
  userRole: Role;
  customerCode: string;
  customerId?: string;
}

class GA4Analytics {
  private initialized = false;

  /**
   * Initialize GA4 with authenticated user context.
   * Sets user ID and user-scoped custom dimensions.
   * Call once after authentication succeeds.
   */
  initialize(context: GA4UserContext): void {
    if (!window.gtag || this.initialized) return;

    // 1. Only run in production environment
    if (!appConfiguration.isProduction) {
      return;
    }

    // 2. Count only real users (External roles)
    // Also exclude internal email domains as a safeguard
    const internalDomains = ['symboticware.com', 'symx.ai'];
    const isInternalEmail = internalDomains.some((domain) =>
      context.userEmail.toLowerCase().endsWith(`@${domain}`)
    );

    if (!isExternalRole(context.userRole) || isInternalEmail) {
      return;
    }

    const measurementId =
      process.env.REACT_APP_GA4_MEASUREMENT_ID ?? 'G-WQLJ0XV3PM';

    console.log(
      `[GA4] Initializing for ${context.userEmail} (${measurementId})`
    );

    // Set user ID for cross-device tracking
    window.gtag('config', measurementId, {
      user_id: context.userId,
      send_page_view: false, // We control page_view events manually
    });

    // Set user-scoped custom dimensions
    window.gtag('set', 'user_properties', {
      customer_code: context.customerCode,
      tenant_id: context.customerId,
      user_email: context.userEmail,
      user_role: context.userRole,
      app_env: 'production',
    });

    this.initialized = true;

    // Emit pending page view if exists
    if ((this as any).pendingPageView) {
      const { pageName, routePath } = (this as any).pendingPageView;
      this.trackPageView(pageName, routePath);
      delete (this as any).pendingPageView;
    }
  }

  /**
   * Track a page view with product_name dimension.
   * Called automatically by the usePageTracking hook.
   */
  trackPageView(pageName: string, routePath: string): void {
    if (!this.isReady()) {
      // Store the last page view if initializing, so we can track it once ready
      (this as any).pendingPageView = { pageName, routePath };
      return;
    }

    window.gtag('event', 'page_view', {
      page_title: pageName,
      page_location: window.location.origin + routePath,
      page_path: routePath,
      product_name: this.getProductNameFromPath(routePath),
    });
  }

  /**
   * Track a feature interaction event.
   * Use for significant UI actions (e.g. exporting a report, changing a view).
   *
   * @param featureName - Human-readable feature name (e.g. 'Control Room')
   * @param action - Action description (e.g. 'export_csv', 'filter_change')
   * @param metadata - Optional extra key-value pairs
   */
  trackFeatureAction(
    featureName: string,
    action: string,
    metadata?: Record<string, string | number | boolean>
  ): void {
    if (!this.isReady()) return;

    window.gtag('event', 'feature_interaction', {
      feature_name: featureName,
      action_detail: action,
      product_name: this.getProductNameFromPath(window.location.pathname),
      ...metadata,
    });
  }

  /**
   * Track a custom event by name.
   * For events that don't fit page_view or feature_interaction.
   */
  trackEvent(
    eventName: string,
    params?: Record<string, string | number | boolean>
  ): void {
    if (!this.isReady()) return;

    window.gtag('event', eventName, {
      product_name: this.getProductNameFromPath(window.location.pathname),
      ...params,
    });
  }

  private getProductNameFromPath(path: string): string {
    const cleanPath = path.toLowerCase();
    if (cleanPath.includes('x-parts')) return 'x-parts';
    if (cleanPath.includes('tire') || cleanPath.includes('tpms'))
      return 'x-tires';
    return 'x-machines';
  }

  private isReady(): boolean {
    return this.initialized && typeof window.gtag === 'function';
  }
}

// Singleton instance
export const ga4Analytics = new GA4Analytics();
